import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    IconArrowLeft,
    IconBrandSpeedtest,
    IconChevronDown,
    IconCopy,
    IconPlus,
    IconRefresh,
    IconSettings,
} from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { HELP_SECTIONS } from './help/helpSections';
import { track } from '../utils/analytics';
import './HelpPage.scss';

const SCROLL_OFFSET = 88;

const HELP_ICONS = {
    copy: IconCopy,
    plus: IconPlus,
    settings: IconSettings,
    speedtest: IconBrandSpeedtest,
    refresh: IconRefresh,
};

function renderPart(part, key) {
    if (part.type === 'text') {
        return <span key={key}>{part.value}</span>;
    }

    if (part.type === 'link') {
        return (
            <Link key={key} to={part.to}>
                {part.value}
            </Link>
        );
    }

    if (part.type === 'external') {
        return (
            <a key={key} href={part.href} target="_blank" rel="noopener noreferrer">
                {part.value}
            </a>
        );
    }

    if (part.type === 'anchor') {
        return (
            <a key={key} href={part.href}>
                {part.value}
            </a>
        );
    }

    if (part.type === 'icon') {
        const Icon = HELP_ICONS[part.name];
        if (!Icon) return null;

        return (
            <span key={key} className="help-page__inline-icon" aria-hidden="true">
                <Icon size={15} stroke={1.5} />
            </span>
        );
    }

    return null;
}

function HelpAccordionItem({ item }) {
    const [open, setOpen] = useState(Boolean(item.defaultOpen));

    return (
        <details
            className={`help-page__accordion-item${open ? ' help-page__accordion-item--open' : ''}`}
            open={open}
            onToggle={(event) => setOpen(event.currentTarget.open)}
        >
            <summary className="help-page__accordion-trigger">
                <span className="help-page__accordion-title">{item.title}</span>
                <IconChevronDown size={18} stroke={1.5} className="help-page__accordion-chevron" aria-hidden="true" />
            </summary>
            <div className="help-page__accordion-body">
                {item.blocks.map((child, index) => (
                    <HelpBlock key={`${item.id}-${index}`} block={child} />
                ))}
            </div>
        </details>
    );
}

function HelpAccordion({ block }) {
    return (
        <div className="help-page__accordion">
            {block.items.map((item) => (
                <HelpAccordionItem key={item.id} item={item} />
            ))}
        </div>
    );
}

function HelpBlock({ block }) {
    if (block.type === 'p') {
        return (
            <p className="help-page__text">
                {block.parts.map((part, index) => renderPart(part, index))}
            </p>
        );
    }

    if (block.type === 'subheading') {
        return (
            <h3 className="help-page__subheading">
                {block.value}
            </h3>
        );
    }

    if (block.type === 'ol') {
        return (
            <ol className="help-page__list help-page__list--ordered">
                {block.items.map((item, index) => (
                    <li key={typeof item === 'string' ? item : `step-${index}`}>
                        {typeof item === 'string'
                            ? item
                            : item.parts.map((part, partIndex) => renderPart(part, partIndex))}
                    </li>
                ))}
            </ol>
        );
    }

    if (block.type === 'list') {
        return (
            <ul className="help-page__list">
                {block.items.map((item) => (
                    <li key={item.label}>
                        <strong>{item.label}</strong>
                        {' — '}
                        {item.href ? (
                            <a href={item.href} target="_blank" rel="noopener noreferrer">
                                {item.text}
                            </a>
                        ) : (
                            item.text
                        )}
                    </li>
                ))}
            </ul>
        );
    }

    if (block.type === 'code') {
        return (
            <code className="help-page__code">
                {block.value}
            </code>
        );
    }

    if (block.type === 'linkGroups') {
        return (
            <div className="help-page__table-wrap">
                <table className="help-page__table">
                    <colgroup>
                        <col className="help-page__table-col help-page__table-col--platform" />
                        <col className="help-page__table-col help-page__table-col--links" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th scope="col">Платформа</th>
                            <th scope="col">Скачать</th>
                        </tr>
                    </thead>
                    <tbody>
                        {block.groups.map((group) => (
                            <tr key={group.label}>
                                <td className="help-page__table-platform">
                                    {group.label}
                                </td>
                                <td className="help-page__table-links-cell">
                                    <ul className="help-page__table-links">
                                        {group.links.map((link) => (
                                            <li key={link.href}>
                                                <a
                                                    href={link.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    {link.text}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    if (block.type === 'accordion') {
        return <HelpAccordion block={block} />;
    }

    return null;
}

function scrollToSection(sectionId) {
    const element = document.getElementById(sectionId);
    if (!element) return;

    const top = element.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
}

export default function HelpPage() {
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        const hash = window.location.hash.replace('#', '');
        if (!hash || !HELP_SECTIONS.some((section) => section.id === hash)) return undefined;

        const timer = setTimeout(() => scrollToSection(hash), 0);
        return () => clearTimeout(timer);
    }, []);

    const handleNavClick = (event, sectionId) => {
        event.preventDefault();
        track('help_section_open', { section_id: sectionId });
        scrollToSection(sectionId);
        window.history.replaceState(null, '', `#${sectionId}`);
    };

    const backTo = isAuthenticated ? '/subscriptions' : '/login';
    const backLabel = isAuthenticated ? 'К моим подпискам' : 'На страницу входа';

    return (
        <div className="help-page">
            <nav className="help-page__mobile-nav" aria-label="Разделы справки">
                {HELP_SECTIONS.map((section) => (
                    <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="help-page__mobile-nav-link"
                        onClick={(event) => handleNavClick(event, section.id)}
                    >
                        {section.title}
                    </a>
                ))}
                <Link to="/links" className="help-page__mobile-nav-link help-page__mobile-nav-link--meta">
                    Ещё
                </Link>
            </nav>

            <div className="help-page__layout">
                <article className="help-page__article">
                    <header className="help-page__hero">
                        <h1 className="help-page__title">Справка</h1>
                        <p className="help-page__subtitle">
                            Всё о подписке, VPN и личном кабинете BypassVPN
                        </p>
                    </header>

                    {HELP_SECTIONS.map((section) => (
                        <section
                            key={section.id}
                            className="help-page__section"
                            aria-labelledby={section.id}
                        >
                            <h2 id={section.id} className="help-page__section-title">
                                {section.title}
                            </h2>
                            <div className="help-page__section-body">
                                {section.blocks.map((block, index) => (
                                    <HelpBlock key={`${section.id}-${index}`} block={block} />
                                ))}
                            </div>
                        </section>
                    ))}
                </article>

                <aside className="help-page__toc" aria-label="На этой странице">
                    <p className="help-page__toc-label">На этой странице</p>
                    <nav className="help-page__toc-nav">
                        {HELP_SECTIONS.map((section) => (
                            <a
                                key={section.id}
                                href={`#${section.id}`}
                                className="help-page__toc-link"
                                onClick={(event) => handleNavClick(event, section.id)}
                            >
                                {section.title}
                            </a>
                        ))}
                    </nav>
                    <div className="help-page__toc-footer">
                        <Link to={backTo} className="help-page__toc-back">
                            <IconArrowLeft size={16} stroke={1.5} />
                            {backLabel}
                        </Link>
                        <Link to="/info" className="help-page__toc-meta">
                            Контакты
                        </Link>
                    </div>
                </aside>
            </div>

            <Link to={backTo} className="btn btn--primary btn--fixed help-page__back">
                <IconArrowLeft size={18} stroke={1.5} />
                {backLabel}
            </Link>

        </div>
    );
}
